import { z } from 'zod';

import { SHA_CONNECTIONS } from './sha.ts';

// The national Facility and Health Worker Registries (docs/plans/phase1-sha.md §7.3, §8.3;
// checklist 13–18). Searches are POSTs with a JSON body, never a query string.

const FACILITY_LEVELS = ['2', '3A', '3B', '4', '5', '6'] as const;

export const facilitySearchRequestSchema = z
  .object({
    mflCode: z.string().trim().min(1).max(40).optional(),
    name: z.string().trim().min(2).max(120).optional(),
    county: z.string().trim().min(2).max(80).optional(),
    level: z.enum(FACILITY_LEVELS).optional(),
  })
  .refine((q) => Object.values(q).some((v) => v !== undefined), {
    message: 'Search by MFL code, name, county or level',
  });
export type FacilitySearchRequest = z.infer<typeof facilitySearchRequestSchema>;

export const registryFacilityViewSchema = z.object({
  registryId: z.string(),
  mflCode: z.string().nullable(),
  name: z.string(),
  /** The registry's level, as it names it. */
  levelDisplay: z.string().nullable(),
  /** Ours, when the specification maps it; null means "can't be compared yet". */
  level: z.enum(FACILITY_LEVELS).nullable(),
  county: z.string().nullable(),
  subCounty: z.string().nullable(),
  status: z.string().nullable(),
});
export type RegistryFacilityView = z.infer<typeof registryFacilityViewSchema>;

export const facilitySearchResponseSchema = z.object({
  items: z.array(registryFacilityViewSchema),
});

export const practitionerSearchRequestSchema = z.object({
  kind: z.enum(['national_id', 'registration_number']),
  value: z.string().trim().min(3).max(40),
});
export type PractitionerSearchRequest = z.infer<typeof practitionerSearchRequestSchema>;

export const registryPractitionerViewSchema = z.object({
  registryId: z.string(),
  names: z.string(),
  cadre: z.string().nullable(),
  council: z.string().nullable(),
  registrationNumber: z.string().nullable(),
  licenceNumber: z.string().nullable(),
  licenceValidTo: z.string().nullable(),
  licenceStatus: z.string().nullable(),
});
export type RegistryPractitionerView = z.infer<typeof registryPractitionerViewSchema>;

export const practitionerSearchResponseSchema = z.object({
  items: z.array(registryPractitionerViewSchema),
});

export const registryLinkRequestSchema = z.object({ registryId: z.string().trim().min(1).max(80) });

export const FACILITY_REGISTRY_CHECKS = [
  'not_linked',
  'matches',
  'level_mismatch',
  'code_mismatch',
  'not_found',
  'level_unmapped',
] as const;

export const facilityRegistryStatusSchema = z.object({
  facilityId: z.uuid(),
  name: z.string(),
  mflCode: z.string().nullable(),
  level: z.enum(FACILITY_LEVELS),
  check: z.enum(FACILITY_REGISTRY_CHECKS),
  /** The comparison in words, with what to do. */
  words: z.string(),
  registry: registryFacilityViewSchema.extend({ fetchedAt: z.iso.datetime() }).nullable(),
});
export type FacilityRegistryStatus = z.infer<typeof facilityRegistryStatusSchema>;

export const practitionerRegistryStatusSchema = z.object({
  userId: z.uuid(),
  displayName: z.string(),
  check: z.enum([
    'matches',
    'licence_mismatch',
    'registry_expired',
    'licence_unknown',
    'not_found',
  ]),
  words: z.string(),
  registry: registryPractitionerViewSchema.extend({ fetchedAt: z.iso.datetime() }),
});
export type PractitionerRegistryStatus = z.infer<typeof practitionerRegistryStatusSchema>;

export const preauthListVersionSchema = z.object({
  id: z.uuid(),
  sourceReference: z.string(),
  status: z.enum(['importing', 'ready', 'active', 'retired']),
  requirementCount: z.number().int(),
  activatedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});

export const shaSetupSchema = z.object({
  connection: z.enum(SHA_CONNECTIONS),
  facility: facilityRegistryStatusSchema,
  practitioners: z.array(practitionerRegistryStatusSchema),
  /** Newest first; the active one decides which services need approval (plan §4.6). */
  preauthLists: z.array(preauthListVersionSchema),
  /** Services and items priced for SHA with no SHA intervention code (plan §7.4). */
  itemsWithoutShaCode: z.number().int(),
});

export const codeCoverageSchema = z.object({
  items: z.array(
    z.object({ id: z.uuid(), code: z.string(), name: z.string(), category: z.string() }),
  ),
});
export type ShaSetupView = z.infer<typeof shaSetupSchema>;
